import React, { useState } from 'react';
import { arrayOf, bool, node, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import { AiOutlineCheckCircle } from 'react-icons/ai';
import { LuChevronDown } from 'react-icons/lu';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { PrimaryButton } from '../../components';

import css from './ListingProgressCard.css';

const CHECKLIST_ID = 'ListingProgressCard.checklist';

/**
 * The editor's progress card: the completeness percent and checklist, and
 * the save button (type="submit", so render it inside the form). From
 * --viewportLarge up it is a sticky side card; below that a bar pinned to the
 * bottom of the screen, where "{n} missing" expands the checklist.
 */
const ListingProgressCardComponent = props => {
  const {
    className,
    completeness,
    saveLabel,
    saveDisabled,
    saveInProgress,
    errorMessage,
    intl,
  } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const { items, percent, missing } = completeness;
  const missingCount = missing.length;

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.header}>
        <span className={css.title}>
          <FormattedMessage id="ListingProgressCard.heading" />
        </span>
        <span className={css.percent}>{`${percent}%`}</span>
        {missingCount > 0 ? (
          <button
            type="button"
            className={css.missingToggle}
            aria-expanded={isExpanded}
            aria-controls={CHECKLIST_ID}
            onClick={() => setIsExpanded(expanded => !expanded)}
          >
            <FormattedMessage
              id="ListingProgressCard.missingToggle"
              values={{ count: missingCount }}
            />
            <LuChevronDown
              className={classNames(css.missingToggleIcon, {
                [css.missingToggleIconOpen]: isExpanded,
              })}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>
      <div
        className={css.bar}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={intl.formatMessage({ id: 'ListingProgressCard.heading' })}
      >
        <div className={css.barFill} style={{ width: `${percent}%` }} />
      </div>
      <ul
        id={CHECKLIST_ID}
        className={classNames(css.checklist, { [css.checklistExpanded]: isExpanded })}
      >
        {items.map(item => (
          <li key={item.key} className={css.checkItem} data-done={item.done}>
            <AiOutlineCheckCircle
              className={item.done ? css.checkIconDone : css.checkIcon}
              aria-hidden="true"
            />
            <FormattedMessage id={`ListingProgressCard.${item.key}`} />
          </li>
        ))}
      </ul>
      {errorMessage ? (
        <p className={css.error} role="alert">
          {errorMessage}
        </p>
      ) : null}
      <PrimaryButton
        type="submit"
        rootClassName={css.saveButton}
        disabled={saveDisabled}
        inProgress={saveInProgress}
      >
        {saveLabel}
      </PrimaryButton>
    </div>
  );
};

ListingProgressCardComponent.defaultProps = {
  className: null,
  saveDisabled: false,
  saveInProgress: false,
  errorMessage: null,
};

ListingProgressCardComponent.propTypes = {
  className: string,
  // getListingCompleteness() result
  completeness: shape({
    items: arrayOf(shape({ key: string.isRequired, done: bool.isRequired })).isRequired,
    percent: number.isRequired,
    missing: arrayOf(string).isRequired,
  }).isRequired,
  saveLabel: node.isRequired,
  saveDisabled: bool,
  saveInProgress: bool,
  // Shown above the save button, e.g. a failed save
  errorMessage: node,
  intl: intlShape.isRequired,
};

const ListingProgressCard = injectIntl(ListingProgressCardComponent);

export default ListingProgressCard;
